import packageJson from '../../package.json';

/**
 * Константы и конфигурация приложения
 *
 * Централизованное место для всех констант приложения:
 * - API URLs и endpoints
 * - Ключи localStorage
 * - Константы игры и турниров
 * - Таймауты и лимиты
 */

/**
 * Определение окружения выполнения
 */
export const ENV = {
	isDevelopment: import.meta.env.DEV,
	isProduction: import.meta.env.PROD,
	mode: import.meta.env.MODE
} as const;

/**
 * API Configuration
 *
 * Приоритет:
 * 1. Переменная окружения VITE_API_BASE_URL (задаётся при сборке)
 * 2. Development fallback: https://localhost:7141/api
 * 3. Production/Staging: window.location.origin/api (фронт и бэк на одном домене через nginx)
 *
 * ВАЖНО: В staging через nginx фронт и API проксируются,
 * поэтому всегда используем текущий origin + /api
 */
const getApiBaseUrl = (): string => {
	// В dev используем VITE_API_BASE_URL или localhost:7141
	if (ENV.isDevelopment) {
		return import.meta.env.VITE_API_BASE_URL || 'https://localhost:7141/api';
	}

	// В production/staging всегда используем текущий origin
	// Это работает потому что nginx проксирует /api на backend
	// Например:
	// - http://localhost:8080 -> http://localhost:8080/api
	// - http://192.168.1.100:8080 -> http://192.168.1.100:8080/api
	// - http://staging.badminton.local:8080 -> http://staging.badminton.local:8080/api
	return `${window.location.origin}/api`;
};

const API_BASE_URL = getApiBaseUrl();

/**
 * API Endpoints
 *
 * Все endpoints для работы с backend API
 */
export const API = {
	baseUrl: API_BASE_URL,
	endpoints: {
		// Базовые пути - /api уже включен в baseUrl
		tournaments: '/tournament',
		games: '/game',

		// Health check - на корневом уровне nginx
		health: '/health'
	}
} as const;

/**
 * Storage Keys
 *
 * Ключи для localStorage/sessionStorage
 * Используем префикс для избежания конфликтов с другими приложениями
 */
export const STORAGE_KEYS = {
	// Access tokens для турниров (Map: tournamentId -> token)
	tournamentTokens: 'badminton_tournament_tokens',

	// ID последнего открытого турнира
	lastTournamentId: 'badminton_last_tournament_id',

	// Настройки пользователя
	userPreferences: 'badminton_user_preferences',

	// Кэш данных для офлайн режима (будущая фича)
	offlineCache: 'badminton_offline_cache'
} as const;

/**
 * Game Constants
 *
 * Константы, связанные с логикой игры и турниров
 */
export const GAME = {
	// Минимальное и максимальное количество кортов
	minCourts: 1,
	maxCourts: 10,

	// Минимальное количество игроков для турнира
	minPlayersDoubles: 4, // Минимум 4 игрока для парного разряда (2 пары)
	minPlayersSingles: 2, // Минимум 2 игрока для одиночного разряда

	// Максимальное количество игроков (разумный лимит для UI)
	maxPlayers: 100,

	// Диапазон счета в игре
	minScore: 0,
	maxScore: 999, // Разумный лимит для валидации

	// Типичный счет до победы (информационно, не обязательно)
	defaultWinningScore: 21
} as const;

/**
 * Tournament Constants
 *
 * Константы для работы с турнирами
 */
export const TOURNAMENT = {
	// Длина имени турнира
	minNameLength: 3,
	maxNameLength: 100,

	// Длина имени игрока
	minPlayerNameLength: 2,
	maxPlayerNameLength: 50,

	// Срок действия турнира (устанавливается на backend)
	// Backend: ExpiresAt = CreatedAt + 4 месяца
	// После истечения турнир доступен только для чтения
	defaultExpirationMonths: 4,

	// Частота автообновления данных турнира (в миллисекундах)
	// 30 секунд для live-турниров
	autoRefreshInterval: 30000
} as const;

/**
 * UI Constants
 *
 * Константы для UI/UX
 */
export const UI = {
	// Таймауты для нотификаций (в миллисекундах)
	notifications: {
		success: 3000,
		error: 5000,
		warning: 4000,
		info: 3000
	},

	// Debounce для поисковых полей (в миллисекундах)
	searchDebounce: 300,

	// Таймаут для запросов (в миллисекундах)
	requestTimeout: 30000, // 30 секунд

	// Количество попыток повтора запроса при ошибке
	maxRetries: 3,

	// Задержка между попытками (в миллисекундах)
	retryDelay: 1000
} as const;

/**
 * Validation Messages
 *
 * Сообщения валидации для форм
 * (можно вынести в отдельный i18n файл в будущем)
 */
export const VALIDATION_MESSAGES = {
	required: 'Это поле обязательно для заполнения',
	tournamentName: `Название турнира должно быть от ${TOURNAMENT.minNameLength} до ${TOURNAMENT.maxNameLength} символов`,
	playerName: `Имя игрока должно быть от ${TOURNAMENT.minPlayerNameLength} до ${TOURNAMENT.maxPlayerNameLength} символов`,
	courtsCount: `Количество кортов должно быть от ${GAME.minCourts} до ${GAME.maxCourts}`,
	minPlayersDoubles: `Минимум ${GAME.minPlayersDoubles} игроков для парного разряда`,
	minPlayersSingles: `Для одиночного разряда требуется минимум ${GAME.minPlayersSingles} игрока`,
	score: `Счёт должен быть от ${GAME.minScore} до ${GAME.maxScore}`,
	duplicatePlayers: 'Имена игроков должны быть уникальными'
} as const;

/**
 * Routes
 *
 * Пути для Vue Router
 * Используем константы для избежания опечаток
 */
export const ROUTES = {
	home: '/',
	createTournament: '/create',
	tournament: '/tournament/:id',
	tournamentView: (id: number) => `/tournament/${id}`,
	notFound: '/:pathMatch(.*)*'
} as const;

/**
 * HTTP Headers
 *
 * Стандартные заголовки для API запросов
 */
export const HTTP_HEADERS = {
	contentType: 'Content-Type',
	authorization: 'Authorization',
	accessToken: 'X-Access-Token'
} as const;

/**
 * Error Messages
 *
 * Общие сообщения об ошибках
 * (можно расширить для i18n в будущем)
 */
export const ERROR_MESSAGES = {
	networkError: 'Ошибка сети. Проверьте подключение к интернету.',
	serverError: 'Ошибка сервера. Попробуйте позже.',
	unauthorized: 'Неверный токен доступа к турниру.',
	notFound: 'Запрашиваемый ресурс не найден.',
	validationError: 'Ошибка валидации данных.',
	unknownError: 'Произошла неизвестная ошибка.'
} as const;

/**
 * Feature Flags
 *
 * Флаги для включения/выключения фич
 * Полезно для постепенного развертывания новых возможностей
 */
export const FEATURES = {
	// Включить PWA функциональность
	enablePWA: false,

	// Включить офлайн режим
	enableOffline: false,

	// Включить real-time обновления через WebSocket/SignalR
	enableRealtime: false,

	// Включить аналитику
	enableAnalytics: false,

	// Показывать debug информацию в development
	showDebugInfo: ENV.isDevelopment
} as const;

/**
 * App Metadata
 *
 * Метаданные приложения
 */
export const APP = {
	name: 'Badminton Americano',
	version: packageJson.version, // Синхронизировано с package.json
	description: 'Tournament management system for Badminton Americano',
	author: 'Emil Zakiev',
	repository: 'https://github.com/ezakiev/BadmintonAmericano'
} as const;
